import type * as React from 'react'
import { useCallback, useEffect, useState } from 'react'

import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { logError } from '~publish/legacy/utils/logError'

import {
  AUTO_CLOSE_TIMEOUT,
  CONTEXT_QUESTION,
  FEEDBACK_API_URL,
  SUMMARY_QUESTION,
} from '../FeedbackWidget.constants'
import type { Attachment } from '../FeedbackWidget.types'
import { useSubmitProductFeedback } from './useSubmitProductFeedback'
import {
  getContextValidationRule,
  getSummaryValidationRule,
  validateContext,
  validateSummary,
} from '../validateFeedback'

type SubmittableEvent = Pick<React.FormEvent<HTMLFormElement>, 'preventDefault'>

interface UseFeedbackFormOptions {
  id: string
  source: string
  organizationId: string | null
  accountId: string | undefined
  email: string | undefined
  initialSummary?: string
  attachments: Attachment[]
  hasUploadingAttachments: boolean
  cleanupAttachments: () => void
  /** Files to the FeatureOS board instead of the Google Sheet. */
  shouldFileToBoard?: boolean
  onSubmit?: () => void
  onAutoClose?: () => void
}

interface UseFeedbackFormReturn {
  summary: string
  setSummary: (value: string) => void
  context: string
  setContext: (value: string) => void
  summaryError: string | null
  contextError: string | null
  error: string | null
  validateSummaryField: () => void
  validateContextField: () => void
  isSubmitting: boolean
  isSubmitted: boolean
  handleSubmit: (event: SubmittableEvent) => void | Promise<void>
  reset: () => void
}

/**
 * Joins the two answers into the single `content` field the feedback pipeline
 * still expects, labelled so a row can be split back into its parts when these
 * submissions are migrated into the Suggestions model.
 */
export function formatFeedbackContent(
  summary: string,
  context: string,
): string {
  return [
    `Q: ${CONTEXT_QUESTION}`,
    context.trim(),
    '',
    `Q: ${SUMMARY_QUESTION}`,
    summary.trim(),
  ].join('\n')
}

export function useFeedbackForm({
  id,
  source,
  organizationId,
  accountId,
  email,
  initialSummary,
  attachments,
  hasUploadingAttachments,
  cleanupAttachments,
  shouldFileToBoard = false,
  onSubmit,
  onAutoClose,
}: UseFeedbackFormOptions): UseFeedbackFormReturn {
  const [summary, setSummaryValue] = useState<string>(initialSummary ?? '')
  const [context, setContextValue] = useState<string>('')
  const [summaryError, setSummaryError] = useState<string | null>(null)
  const [contextError, setContextError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const { submitProductFeedback } = useSubmitProductFeedback()

  // Clear a field's error as soon as it is edited, so the message does not sit
  // there contradicting what the person is currently typing.
  const setSummary = useCallback((value: string): void => {
    setSummaryValue(value)
    setSummaryError(null)
  }, [])

  const setContext = useCallback((value: string): void => {
    setContextValue(value)
    setContextError(null)
  }, [])

  // Blur only validates a field someone has actually typed in. Flagging an
  // empty field the moment focus passes through it would tell people off for
  // a question they have not reached yet; missing answers surface on submit.
  const validateSummaryField = useCallback((): void => {
    if (summary.trim().length === 0) {
      setSummaryError(null)
      return
    }
    setSummaryError(validateSummary(summary))
  }, [summary])

  const validateContextField = useCallback((): void => {
    if (context.trim().length === 0) {
      setContextError(null)
      return
    }
    setContextError(validateContext(context))
  }, [context])

  const reset = useCallback((): void => {
    setSummaryValue(initialSummary ?? '')
    setContextValue('')
    setSummaryError(null)
    setContextError(null)
    setError(null)
    setIsSubmitted(false)
    setIsSubmitting(false)
    cleanupAttachments()
  }, [cleanupAttachments, initialSummary])

  // Submit is always available, so this is where an unanswered or unusable
  // question is named. Disabling the button instead would leave someone
  // guessing which of the two questions it is waiting on.
  const handleSubmit = async (event: SubmittableEvent): Promise<void> => {
    event.preventDefault()

    const nextSummaryError = validateSummary(summary)
    const nextContextError = validateContext(context)
    setSummaryError(nextSummaryError)
    setContextError(nextContextError)

    if (nextSummaryError || nextContextError) {
      const summaryValidationError = getSummaryValidationRule(summary)
      const contextValidationError = getContextValidationRule(context)

      BufferTracker.feedbackFormValidationFailed({
        clientName: 'publishWeb',
        organizationId: organizationId ?? '',
        userId: accountId ?? '',
        widgetId: id,
        source,
        ...(summaryValidationError ? { summaryValidationError } : {}),
        ...(contextValidationError ? { contextValidationError } : {}),
      })

      return
    }

    if (hasUploadingAttachments) {
      setError('Please wait for attachments to finish uploading')
      return
    }

    const failedAttachments = attachments.filter((a) => a.uploadError)
    if (failedAttachments.length > 0) {
      setError(
        'Some attachments failed to upload. Please remove them and try again.',
      )
      return
    }

    setError(null)
    setIsSubmitting(true)

    const content = formatFeedbackContent(summary, context)

    try {
      const attachmentLinks = attachments.map((a) => a.uploadedUrl)

      if (shouldFileToBoard) {
        await submitProductFeedback({
          summary: summary.trim(),
          context: context.trim(),
          widgetId: id,
          source,
          sourceUrl: window.location.href,
          attachmentUrls: attachmentLinks.filter(
            (url): url is string => typeof url === 'string',
          ),
        })
      } else {
        const response = await fetch(FEEDBACK_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            content,
            // Sent alongside `content` so the sheet can gain columns for the two
            // answers without another frontend change. Unknown keys are ignored
            // by the Apps Script that builds the row. Named for where they end up
            // in the Suggestions model rather than for the questions that produced
            // them, so the eventual migration is a straight copy.
            title: summary.trim(),
            description: context.trim(),
            organizationId: organizationId ?? '',
            accountId: accountId ?? '',
            email: email ?? '',
            sourceUrl: window.location.href,
            category: null,
            source,
            widgetId: id,
            attachmentLinks,
          }),
        })

        const result = await response.json()

        if (!result.success) {
          setError(result.error || 'Failed to submit feedback')
          setIsSubmitting(false)
          return
        }
      }

      BufferTracker.feedbackFormSubmitted({
        clientName: 'publishWeb',
        organizationId: organizationId ?? '',
        userId: accountId ?? '',
        widgetId: id,
        source,
        text: content,
      })

      setIsSubmitting(false)
      setIsSubmitted(true)
      onSubmit?.()
    } catch (submissionError) {
      // Only the board path reports: FeatureOS is a live dependency of a
      // submission, where the Worker was fire-and-forget, and the Sheet path is
      // being retired rather than instrumented. The feedback itself and the
      // attachment URLs stay out of Sentry.
      if (shouldFileToBoard) {
        logError(
          submissionError instanceof Error
            ? submissionError
            : new Error(String(submissionError)),
          {
            context: 'feedbackWidget',
            metaData: { widgetId: id, source },
          },
        )
      }

      setError('We could not send your feedback. Please try again.')
      setIsSubmitting(false)
    }
  }

  // Auto-close after submission
  useEffect(() => {
    if (isSubmitted) {
      const timer = setTimeout(() => {
        reset()
        onAutoClose?.()
      }, AUTO_CLOSE_TIMEOUT)

      return (): void => {
        clearTimeout(timer)
      }
    }
  }, [isSubmitted, reset, onAutoClose])

  return {
    summary,
    setSummary,
    context,
    setContext,
    summaryError,
    contextError,
    error,
    validateSummaryField,
    validateContextField,
    isSubmitting,
    isSubmitted,
    handleSubmit,
    reset,
  }
}
